/**
 * Centralized API configuration with interceptors and caching
 */
import axios from 'axios';
import logger from './logger';
import apiCache from './apiCache';

const API_TIMEOUT = 30000; // 30 seconds
const MAX_RETRIES = 3;
const RETRY_DELAY = 1000; // 1 second

// Cache configuration
const CACHE_ENABLED = import.meta.env.VITE_ENABLE_API_CACHE !== 'false';
const CACHEABLE_METHODS = ['GET'];

/**
 * Extract user ID from JWT token for cache key isolation
 * SECURITY: Prevents cross-user cache data leakage
 */
const getUserIdFromToken = (token) => {
  if (!token || typeof token !== 'string') return null;

  try {
    // JWT format: header.payload.signature
    const parts = token.split('.');
    if (parts.length !== 3) return null;

    // Decode payload (middle part) - base64url encoded JSON
    const payload = parts[1];
    // Replace base64url chars with base64 chars
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = atob(base64);
    const decoded = JSON.parse(jsonPayload);

    // Extract user identifier (could be user_id, userId, sub, or email)
    return decoded.user_id || decoded.userId || decoded.sub || decoded.email || null;
  } catch (error) {
    logger.warn('Failed to decode JWT token for cache key:', error);
    return null;
  }
};

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: import.meta.env.VITE_BACKEND_URL || '',
  timeout: API_TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor
apiClient.interceptors.request.use(
  async (config) => {
    // Add auth token if exists - check nominee token first to avoid using regular user token
    const token = localStorage.getItem('nomineeToken') ||
                  localStorage.getItem('adminToken') ||
                  localStorage.getItem('accessToken') ||
                  localStorage.getItem('token');

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      logger.log('✅ Token attached to request:', config.url, 'Token:', token.substring(0, 20) + '...');
    } else {
      logger.warn('⚠️ No token found in localStorage for request:', config.url);
    }

    // Check cache for GET requests
    if (CACHE_ENABLED && CACHEABLE_METHODS.includes(config.method?.toUpperCase())) {
      // SECURITY FIX: Include user ID in cache key to prevent cross-user data leakage
      const userId = getUserIdFromToken(token);
      const cacheKey = apiCache.generateKey(config.url, config.params, userId);
      const cachedResponse = apiCache.get(cacheKey);

      if (cachedResponse) {
        logger.log('💾 Cache HIT:', config.url, userId ? `[User: ${userId.substring(0, 8)}...]` : '');

        // Return cached response directly (cancel actual request)
        config.adapter = () => {
          return Promise.resolve({
            data: cachedResponse,
            status: 200,
            statusText: 'OK',
            headers: {},
            config,
            request: {},
            fromCache: true
          });
        };
      }
    }

    logger.log('API Request:', config.method?.toUpperCase(), config.url);
    return config;
  },
  (error) => {
    logger.error('Request Error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor with retry logic and caching
apiClient.interceptors.response.use(
  (response) => {
    logger.log('API Response:', response.status, response.config.url);

    // Cache successful GET responses
    if (
      CACHE_ENABLED &&
      CACHEABLE_METHODS.includes(response.config.method?.toUpperCase()) &&
      response.status === 200 &&
      !response.fromCache // Don't re-cache cached responses
    ) {
      // SECURITY FIX: Include user ID in cache key to prevent cross-user data leakage
      const authHeader = response.config.headers?.Authorization;
      const token = authHeader?.replace('Bearer ', '');
      const userId = getUserIdFromToken(token);
      const cacheKey = apiCache.generateKey(response.config.url, response.config.params, userId);
      apiCache.set(cacheKey, response.data);
      logger.log('💾 Cached response:', response.config.url, userId ? `[User: ${userId.substring(0, 8)}...]` : '');
    }

    // Invalidate cache for mutation requests (POST, PUT, DELETE, PATCH)
    if (
      CACHE_ENABLED &&
      ['POST', 'PUT', 'DELETE', 'PATCH'].includes(response.config.method?.toUpperCase())
    ) {
      // Extract the resource endpoint (e.g., 'credentials', 'cards', 'financial')
      const url = response.config.url;
      const pathParts = url.split('/').filter(part => part); // Remove empty parts

      // Find the resource name - typically the API endpoint name
      // Examples: /api/credentials, /api/cards/123, /api/financial/fetch
      // We want to extract 'credentials', 'cards', 'financial'
      const apiIndex = pathParts.indexOf('api');
      const resourceName = apiIndex >= 0 && pathParts.length > apiIndex + 1
        ? pathParts[apiIndex + 1]
        : pathParts[pathParts.length - 1];

      // Invalidate ALL cache entries that contain this resource name
      // This ensures all related GET requests are cleared (fetch, list, etc.)
      const invalidated = apiCache.invalidatePattern(resourceName);
      if (invalidated.length > 0) {
        logger.log('💾 Cache invalidated:', invalidated.length, 'entries for resource:', resourceName);
      } else {
        logger.warn('⚠️ No cache entries found to invalidate for:', resourceName, 'URL:', url);
      }
    }

    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    
    // Don't retry if already retried max times
    if (!originalRequest._retry) {
      originalRequest._retry = 0;
    }
    
    // Retry logic for network errors or 5xx errors
    if (
      originalRequest._retry < MAX_RETRIES &&
      (!error.response || error.response.status >= 500)
    ) {
      originalRequest._retry += 1;
      
      logger.warn(
        `Retrying request (${originalRequest._retry}/${MAX_RETRIES}):`,
        originalRequest.url
      );
      
      // Wait before retrying
      await new Promise(resolve => setTimeout(resolve, RETRY_DELAY * originalRequest._retry));
      
      return apiClient(originalRequest);
    }
    
    // Handle specific error cases
    if (error.response) {
      const { status, data } = error.response;

      // Note: 401 errors are handled by SessionContext interceptor
      // to avoid duplicate logout handling

      // Forbidden
      if (status === 403) {
        logger.error('Access Forbidden:', data);
        // Auth/pre-auth endpoints must keep the ORIGINAL error (status + detail)
        // so the login/activation pages can show a specific message — e.g. a
        // pending-approval user needs "Account pending approval", not a generic
        // permission error. Mirrors SessionContext's 401 auth-endpoint skip.
        const url = originalRequest?.url || '';
        const isAuthEndpoint = url.includes('/auth/') ||
                               url.includes('/admin') ||
                               url.includes('/nominee/login') ||
                               url.includes('/nominee/activate');
        if (isAuthEndpoint) {
          return Promise.reject(error);
        }
        return Promise.reject(new Error('You do not have permission to access this resource.'));
      }

      // Rate Limited
      if (status === 429) {
        logger.error('Rate Limited:', data);
        return Promise.reject(new Error('Too many requests. Please try again later.'));
      }

      // Server Error
      if (status >= 500) {
        logger.error('Server Error:', status, data);
        return Promise.reject(new Error('Server error. Please try again later.'));
      }

      logger.error('API Error:', status, data);
    } else if (error.request) {
      // Network error
      logger.error('Network Error:', error.message);
      return Promise.reject(new Error('Network error. Please check your connection.'));
    } else {
      logger.error('Request Setup Error:', error.message);
    }
    
    return Promise.reject(error);
  }
);

// Export cache utilities for manual cache management
export { apiCache };

export default apiClient;
